(() => {
  const VARIANT_CHANGE_EVENT = 'action:update:pickupLocationOnVariantChange';
  const TRIGGER_CART_CHANGE_EVENT = 'action:cart:updateCart';
  const TOAST_EVENT = 'showToastMessage';

  const safeJsonParse = (str, fallback = null) => {
    try {
      return JSON.parse(str);
    } catch {
      return fallback;
    }
  };

  const MEASUREMENT_PATTERNS = [
    {
      kind: 'mass',
      factor: 1000,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:kg|kgs|kilograms?|kilos?)\b/gi,
    },
    {
      kind: 'mass',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:g|gm|gms|grams?)\b/gi,
    },
    {
      kind: 'volume',
      factor: 1000,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:l|litres?|liters?)\b/gi,
    },
    {
      kind: 'volume',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:ml|millilitres?|milliliters?)\b/gi,
    },
    {
      kind: 'serve',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:serves?|servings?|srv)\b/gi,
    },
    {
      kind: 'capsule',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:capsules?|caps?)\b/gi,
    },
    {
      kind: 'tablet',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:tablets?|tabs?)\b/gi,
    },
    {
      kind: 'sachet',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:sachets?|packets?)\b/gi,
    },
    {
      kind: 'scoop',
      factor: 1,
      regex: /(\d+(?:[.,]\d+)?)\s*(?:scoops?)\b/gi,
    },
  ];

  const extractMeasurements = (value) => {
    if (value === null || value === undefined) return [];

    const text = String(value).replace(/\u00a0/g, ' ');
    const measurements = [];

    MEASUREMENT_PATTERNS.forEach((pattern) => {
      pattern.regex.lastIndex = 0;

      let match = pattern.regex.exec(text);

      while (match) {
        const numericValue = Number(String(match[1]).replace(',', '.'));

        if (!Number.isNaN(numericValue) && numericValue > 0) {
          measurements.push({
            kind: pattern.kind,
            value: numericValue * pattern.factor,
          });
        }

        match = pattern.regex.exec(text);
      }
    });

    return measurements.filter((measurement, index, all) => {
      return !all.slice(0, index).some((existing) => {
        return (
          existing.kind === measurement.kind &&
          Math.abs(existing.value - measurement.value) < 0.001
        );
      });
    });
  };

  const getVariantStrings = (variant) => {
    if (!variant) return [];

    const strings = [];

    if (Array.isArray(variant.options)) strings.push(...variant.options);
    if (variant.option1) strings.push(variant.option1);
    if (variant.option2) strings.push(variant.option2);
    if (variant.option3) strings.push(variant.option3);
    if (variant.title) strings.push(variant.title);

    return strings.filter(Boolean);
  };

  const findVariantMeasurement = (variant, baseMeasurement) => {
    if (!variant || !baseMeasurement) return null;

    const strings = getVariantStrings(variant);

    for (const value of strings) {
      const match = extractMeasurements(value).find((measurement) => {
        return measurement.kind === baseMeasurement.kind;
      });

      if (match) return match;
    }

    return null;
  };

  const measurementsEqual = (a, b) => {
    return !!(
      a &&
      b &&
      a.kind === b.kind &&
      Math.abs(a.value - b.value) < 0.001
    );
  };

  const findBaseVariant = (product, baseMeasurement) => {
    if (!product || !Array.isArray(product.variants) || !baseMeasurement) return null;

    return product.variants.find((variant) => {
      if (!variant || variant.available === false) return false;

      return measurementsEqual(
        findVariantMeasurement(variant, baseMeasurement),
        baseMeasurement
      );
    }) || null;
  };

  const getMixCount = (variant, baseMeasurement) => {
    if (!variant || !baseMeasurement || !baseMeasurement.value) return 0;

    const selectedMeasurement = findVariantMeasurement(variant, baseMeasurement);

    if (!selectedMeasurement || selectedMeasurement.kind !== baseMeasurement.kind) return 0;

    const ratio = selectedMeasurement.value / baseMeasurement.value;
    const roundedRatio = Math.round(ratio);

    if (roundedRatio <= 1) return 0;
    if (Math.abs(ratio - roundedRatio) > 0.001) return 0;

    return roundedRatio;
  };

  class MixMatchFlavours extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.allowDuplicates = String(this.dataset.allowDuplicates) === 'true';
      this.autoShow = String(this.dataset.autoShow) === 'true';
      this.flavours = [];
      this.currentVariant = null;
      this.mixCount = 0;
      this.baseSize = String(this.dataset.baseSize || '').trim();
      this.baseMeasurement = extractMeasurements(this.baseSize)[0] || null;

      /*
       * Manual mode keeps the existing journey: the selectors start closed and
       * the customer opts in with the toggle button.
       *
       * Auto mode starts enabled so a compatible size immediately reveals the
       * selectors. If the customer opts back to a single flavour, that choice is
       * respected while they move between compatible sizes. Leaving the
       * compatible-size range resets the default for the next eligible size.
       */
      this.enabled = this.autoShow;

      this.toggleOpenLabel = this.dataset.toggleLabel || 'Yes — let me choose';
      this.singleFlavourLabel = this.dataset.singleFlavourLabel || 'No — single flavour only';
      this.toggleCloseLabel = this.autoShow ? this.singleFlavourLabel : 'No thanks';

      this._bundleAddInFlight = false;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onAdd = this.onAdd.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onInfoOpen = this.onInfoOpen.bind(this);
      this.onInfoClose = this.onInfoClose.bind(this);
      this.onInfoDialogClick = this.onInfoDialogClick.bind(this);
    }

    connectedCallback() {
      const jsonEl = this.querySelector('[data-mmf-json]');
      const linkedProducts = safeJsonParse((jsonEl && jsonEl.textContent) || '[]', []);

      this.flavours = linkedProducts
        .map((item) => {
          if (!item) return null;

          if (item.variant_id) return item;

          const baseVariant = findBaseVariant(item, this.baseMeasurement);

          if (!baseVariant) return null;

          return Object.assign({}, item, {
            variant_id: baseVariant.id,
            available: baseVariant.available,
          });
        })
        .filter((x) => x && x.variant_id)
        .filter((x) => x.available !== false)
        .sort((a, b) => {
          const aOrder = Number(a.sort_order);
          const bOrder = Number(b.sort_order);

          const aHasOrder = !Number.isNaN(aOrder);
          const bHasOrder = !Number.isNaN(bOrder);

          if (aHasOrder && bHasOrder && aOrder !== bOrder) return aOrder - bOrder;
          if (aHasOrder && !bHasOrder) return -1;
          if (!aHasOrder && bHasOrder) return 1;

          return String(a.product_title || '').localeCompare(String(b.product_title || ''));
        });

      this.shell = this.querySelector('.mmf__shell');
      this.toggleBtn = this.querySelector('[mmf-toggle]');
      this.panel = this.querySelector('[mmf-panel]');
      this.selectorsWrap = this.querySelector('[mmf-selectors]');
      this.addBtn = this.querySelector('[mmf-add]');
      this.note = this.querySelector('[mmf-note]');
      this.infoOpenBtn = this.querySelector('[mmf-info-open]');
      this.infoDialog = this.querySelector('[mmf-info-dialog]');
      this.infoCloseBtn = this.querySelector('[mmf-info-close]');

      if (this.toggleBtn) this.toggleBtn.addEventListener('click', this.onToggle);
      if (this.addBtn) this.addBtn.addEventListener('click', this.onAdd);
      if (this.infoOpenBtn) this.infoOpenBtn.addEventListener('click', this.onInfoOpen);
      if (this.infoCloseBtn) this.infoCloseBtn.addEventListener('click', this.onInfoClose);
      if (this.infoDialog) this.infoDialog.addEventListener('click', this.onInfoDialogClick);

      this.productInfo = this.closest('[product-info]') || document;
      this.productInfo.addEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);

      this.form = this.getProductForm();

      if (this.form) {
        this.form
          .querySelectorAll(
            '.quantity__button, .quantity button, quantity-input button, quantity-selector button, [data-quantity-plus], [data-quantity-minus], [increase-value], [decrease-value]'
          )
          .forEach((btn) => {
            if (!btn.getAttribute('type') || btn.getAttribute('type') === 'submit') {
              btn.setAttribute('type', 'button');
            }
          });
      }

      if (this.form) this.form.addEventListener('submit', this.onFormSubmit, true);

      const initialVariant = this.getInitialVariantFromDom();
      if (initialVariant) this.currentVariant = initialVariant;

      this.syncVisibility();
      this.updateToggleLabel();
    }

    disconnectedCallback() {
      if (this.toggleBtn) this.toggleBtn.removeEventListener('click', this.onToggle);
      if (this.addBtn) this.addBtn.removeEventListener('click', this.onAdd);
      if (this.infoOpenBtn) this.infoOpenBtn.removeEventListener('click', this.onInfoOpen);
      if (this.infoCloseBtn) this.infoCloseBtn.removeEventListener('click', this.onInfoClose);
      if (this.infoDialog) this.infoDialog.removeEventListener('click', this.onInfoDialogClick);
      if (this.productInfo) this.productInfo.removeEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
      if (this.form) this.form.removeEventListener('submit', this.onFormSubmit, true);
    }

    onInfoOpen() {
      if (!this.infoDialog) return;

      if (typeof this.infoDialog.showModal === 'function') {
        if (!this.infoDialog.open) this.infoDialog.showModal();
      } else {
        this.infoDialog.setAttribute('open', '');
      }
    }

    onInfoClose() {
      if (!this.infoDialog) return;

      if (typeof this.infoDialog.close === 'function' && this.infoDialog.open) {
        this.infoDialog.close();
      } else {
        this.infoDialog.removeAttribute('open');
      }

      if (this.infoOpenBtn) this.infoOpenBtn.focus();
    }

    onInfoDialogClick(event) {
      if (!this.infoDialog || event.target !== this.infoDialog) return;
      this.onInfoClose();
    }

    updateToggleLabel() {
      if (!this.toggleBtn) return;
      this.toggleBtn.textContent = this.enabled ? this.toggleCloseLabel : this.toggleOpenLabel;
    }

    getInitialVariantFromDom() {
      const root = this.closest('[product-info]');
      const script = root
        ? root.querySelector('[variant-block] script[type="application/json"]')
        : null;

      return safeJsonParse((script && script.textContent) || '', null);
    }

    getProductForm() {
      const root = this.closest('[product-info]') || document;

      return (
        root.querySelector(`form#product-form-${this.sectionId}`) ||
        root.querySelector('form[id^="product-form-"]')
      );
    }

    getProductRoot() {
      return this.closest('[product-info]') || this.closest('section') || document;
    }

    getInputValue(el) {
      if (!el) return null;

      if (el.matches && el.matches('input[type="radio"], input[type="checkbox"]') && !el.checked) {
        return null;
      }

      const candidates = [
        el.dataset ? el.dataset.sellingPlanId : null,
        el.getAttribute('data-selling-plan-id'),
        el.getAttribute('selling-plan-id'),
        el.value,
        el.getAttribute('value'),
      ];

      const invalidValues = [
        'null',
        'undefined',
        'false',
        '0',
        'on',
        'one_time',
        'one_time_purchase',
        'onetime',
        'one-time',
      ];

      for (const candidate of candidates) {
        if (!candidate) continue;

        const normalised = String(candidate).trim();
        if (!normalised) continue;
        if (invalidValues.includes(normalised.toLowerCase())) continue;

        return normalised;
      }

      return null;
    }

    getSellingPlanId() {
      const form = this.getProductForm();
      const root = this.getProductRoot();
      const scopes = [root, form].filter(Boolean);

      const cleanValue = (value) => {
        if (value === null || value === undefined) return null;

        const normalised = String(value).trim();

        if (!normalised) return null;

        const invalidValues = [
          'null',
          'undefined',
          'false',
          '0',
          'on',
          'one_time',
          'one_time_purchase',
          'one-time',
          'onetime',
          'one time purchase',
        ];

        if (invalidValues.includes(normalised.toLowerCase())) return null;

        return normalised;
      };

      const getSellingPlanValue = (el) => {
        if (!el) return null;

        return cleanValue(
          el.getAttribute('data-selling-plan-id') ||
            el.getAttribute('selling-plan-id') ||
            (el.dataset ? el.dataset.sellingPlanId : null) ||
            el.value ||
            el.getAttribute('value')
        );
      };

      const safeQuery = (scope, selector) => {
        try {
          return scope.querySelector(selector);
        } catch {
          return null;
        }
      };

      const oneTimeSelectors = [
        'input[type="radio"][data-radio-type="one_time_purchase"]:checked',
        'input[type="radio"][value="one_time_purchase"]:checked',
        'input[type="radio"][value="one-time"]:checked',
        'input[type="radio"][value="onetime"]:checked',
        'input[type="radio"][value="one_time"]:checked',
        'input[type="radio"][name*="purchase"][value*="one" i]:checked',
        'input[type="radio"][name*="subscription"][value*="one" i]:checked',
        'input[type="radio"][name*="selling_plan"][value=""]:checked',
        '[class*="one-time" i] input[type="radio"]:checked',
        '[class*="one_time" i] input[type="radio"]:checked',
        '[class*="onetime" i] input[type="radio"]:checked',
      ];

      const checkedSellingPlanSelectors = [
        'input[type="radio"][data-radio-type="selling_plan"]:checked',
        'input[type="radio"][data-selling-plan-id]:checked',
        'input[type="radio"][selling-plan-id]:checked',
        'input[type="radio"][name="selling_plan"]:checked',
      ];

      const selectedSubscriptionSelectors = [
        '.subs-is-selected',
        '[class*="subscribe" i][class*="selected" i]',
        '[class*="subscription" i][class*="selected" i]',
        '[class*="selling-plan" i][class*="selected" i]',
        '[class*="selling_plan" i][class*="selected" i]',
        '[class*="loop" i][class*="selected" i]',
        '[aria-checked="true"][data-selling-plan-id]',
        '[data-selected="true"][data-selling-plan-id]',
      ];

      for (const scope of scopes) {
        if (!scope) continue;

        const oneTimeSelected = oneTimeSelectors.some((selector) => {
          return !!safeQuery(scope, selector);
        });

        if (oneTimeSelected) {
          return null;
        }
      }

      for (const scope of scopes) {
        if (!scope) continue;

        for (const selector of checkedSellingPlanSelectors) {
          const checkedPlan = safeQuery(scope, selector);
          const sellingPlanId = getSellingPlanValue(checkedPlan);

          if (sellingPlanId) return sellingPlanId;
        }
      }

      for (const scope of scopes) {
        if (!scope) continue;

        let selectedSubscriptionBlock = null;

        for (const selector of selectedSubscriptionSelectors) {
          selectedSubscriptionBlock = safeQuery(scope, selector);

          if (selectedSubscriptionBlock) break;
        }

        if (!selectedSubscriptionBlock) continue;

        const selectedFrequency =
          (selectedSubscriptionBlock.querySelector &&
            selectedSubscriptionBlock.querySelector('.subs-frequency-select')) ||
          (scope.querySelector && scope.querySelector('.subs-frequency-select')) ||
          null;

        const frequencyValue = getSellingPlanValue(selectedFrequency);
        if (frequencyValue) return frequencyValue;

        const selectedPlanInsideBlock =
          selectedSubscriptionBlock.querySelector &&
          selectedSubscriptionBlock.querySelector(
            'input[data-selling-plan-id], input[selling-plan-id], input[name="selling_plan"], select[name="selling_plan"]'
          );

        const blockValue = getSellingPlanValue(selectedPlanInsideBlock);
        if (blockValue) return blockValue;

        if (form) {
          const formDataValue = cleanValue(new FormData(form).get('selling_plan'));
          if (formDataValue) return formDataValue;
        }
      }

      if (form) {
        const formDataValue = cleanValue(new FormData(form).get('selling_plan'));
        if (formDataValue) return formDataValue;

        const hiddenSellingPlan = form.querySelector(
          'input[name="selling_plan"], select[name="selling_plan"]'
        );

        const hiddenValue = getSellingPlanValue(hiddenSellingPlan);
        if (hiddenValue) return hiddenValue;
      }

      return null;
    }

    getSubscriptionProperties() {
      const form = this.getProductForm();
      const root = this.getProductRoot();
      const scopes = [form, root].filter(Boolean);
      const properties = new Map();

      scopes.forEach((scope) => {
        scope.querySelectorAll('[name^="properties["]').forEach((field) => {
          if (field.disabled) return;
          if (field.matches('input[type="radio"], input[type="checkbox"]') && !field.checked) return;

          const match = field.name.match(/^properties\[(.+)\]$/);
          if (!match || !match[1]) return;

          const key = match[1];
          const value = field.value;

          if (value === null || value === undefined || String(value).trim() === '') return;

          properties.set(key, value);
        });
      });

      return Array.from(properties.entries()).map(([key, value]) => ({ key, value }));
    }

    onVariantChange(e) {
      if (
        e &&
        e.detail &&
        e.detail.sectionId &&
        this.sectionId &&
        String(e.detail.sectionId) !== String(this.sectionId)
      ) {
        return;
      }

      if (e && e.detail && e.detail.variant) {
        this.currentVariant = e.detail.variant;
        this.syncVisibility();
      }
    }

    syncVisibility() {
      this.mixCount = getMixCount(this.currentVariant, this.baseMeasurement);

      const shouldShow =
        this.mixCount > 1 &&
        this.flavours.length > 0;

      if (!shouldShow) {
        if (this.shell) this.shell.hidden = true;

        /*
         * Reset to the configured default whenever the customer leaves the
         * compatible-size range. In auto mode this means selecting a compatible
         * size again will open Mix & Match automatically; in manual mode it
         * remains closed exactly as before.
         */
        this.enabled = this.autoShow;

        if (this.panel) this.panel.hidden = true;
        this.clearNote();
        this.updateToggleLabel();
        return;
      }

      if (this.shell) this.shell.hidden = false;

      if (this.enabled) {
        if (this.panel) this.panel.hidden = false;
        this.buildSelectors();
      } else {
        if (this.panel) this.panel.hidden = true;
      }

      this.updateToggleLabel();
    }

    onToggle() {
      this.enabled = !this.enabled;

      if (this.panel) this.panel.hidden = !this.enabled;

      this.clearNote();
      this.updateToggleLabel();

      if (this.enabled) this.buildSelectors();
    }

    shuffleArray(arr) {
      const a = [...arr];

      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));

        [a[i], a[j]] = [a[j], a[i]];
      }

      return a;
    }

    buildSelectors() {
      if (!this.selectorsWrap) return;

      this.selectorsWrap.innerHTML = '';

      const selectorLabel = this.dataset.selectorLabel || 'Choose flavour';

      for (let i = 0; i < this.mixCount; i++) {
        const row = document.createElement('div');
        row.className = 'mmf__row';

        const label = document.createElement('label');
        label.className = 'mmf__label';
        label.textContent = `${selectorLabel} ${i + 1}`;

        const select = document.createElement('select');
        select.className = 'mmf__select input';
        select.setAttribute('mmf-select', '');
        select.dataset.index = String(i);

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select a flavour';
        placeholder.selected = true;
        placeholder.disabled = true;

        select.appendChild(placeholder);

        const flavoursAlphabetical = [...this.flavours].sort((a, b) => {
          return String(a.product_title || '').localeCompare(String(b.product_title || ''));
        });

        flavoursAlphabetical.forEach((f) => {
          const opt = document.createElement('option');

          opt.value = String(f.variant_id);

          const tag = (f.dropdown_tag || '').trim();
          opt.textContent = tag ? `${f.product_title} (${tag})` : f.product_title;

          select.appendChild(opt);
        });

        select.addEventListener('change', () => {
          if (!this.allowDuplicates) this.enforceNoDuplicates();

          this.clearNote();
        });

        const selectWrapper = document.createElement('div');
        selectWrapper.className = 'select-wrapper';

        select.classList.add('select');

        const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        arrowSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        arrowSvg.setAttribute('width', '17');
        arrowSvg.setAttribute('height', '9');
        arrowSvg.setAttribute('viewBox', '0 0 17 9');
        arrowSvg.setAttribute('fill', 'none');

        const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        arrowPath.setAttribute('d', 'M1 8.5L8.5 0.999999L16 8.5');
        arrowPath.setAttribute('stroke', 'rgba(var(--color-input-variant-color))');
        arrowPath.setAttribute('stroke-width', 'var(--icon-thickness)');

        arrowSvg.appendChild(arrowPath);

        selectWrapper.appendChild(select);
        selectWrapper.appendChild(arrowSvg);

        row.appendChild(label);
        row.appendChild(selectWrapper);

        this.selectorsWrap.appendChild(row);
      }

      const selects = Array.from(this.querySelectorAll('select[mmf-select]'));
      const currentHandle = this.dataset.currentHandle;
      const usedPrefillIds = new Set();

      if (currentHandle) {
        const currentMatch = this.flavours.find((f) => f.product_handle === currentHandle);

        if (currentMatch && currentMatch.variant_id) {
          const firstSelect = selects.find((s) => s.dataset.index === '0');

          if (firstSelect && !firstSelect.value) {
            firstSelect.value = String(currentMatch.variant_id);
          }

          usedPrefillIds.add(String(currentMatch.variant_id));
        }
      }

      const getSortOrder = (f) => {
        const n = Number(f.sort_order);

        return Number.isNaN(n) ? 999999 : n;
      };

      const groupedByOrder = this.flavours
        .filter((f) => f && f.variant_id)
        .filter((f) => !usedPrefillIds.has(String(f.variant_id)))
        .reduce((groups, f) => {
          const order = String(getSortOrder(f));

          if (!groups[order]) groups[order] = [];

          groups[order].push(f);

          return groups;
        }, {});

      const orderedPool = Object.keys(groupedByOrder)
        .map((order) => Number(order))
        .sort((a, b) => a - b)
        .reduce((pool, order) => {
          const group = groupedByOrder[String(order)] || [];

          return pool.concat(this.shuffleArray(group));
        }, []);

      for (let i = 1; i < selects.length; i++) {
        const sel = selects[i];

        if (!sel || sel.value) continue;

        const next = orderedPool.shift();

        if (!next || !next.variant_id) continue;

        sel.value = String(next.variant_id);
        usedPrefillIds.add(String(next.variant_id));
      }

      if (!this.allowDuplicates) this.enforceNoDuplicates();
    }

    enforceNoDuplicates() {
      const selects = Array.from(this.querySelectorAll('select[mmf-select]'));
      const chosen = new Set(selects.map((s) => s.value).filter(Boolean));

      selects.forEach((select) => {
        Array.from(select.options).forEach((opt) => {
          if (!opt.value) return;

          const isChosenElsewhere = chosen.has(opt.value) && opt.value !== select.value;

          opt.disabled = isChosenElsewhere;
        });
      });
    }

    setNote(message) {
      if (!this.note) return;

      this.note.hidden = false;
      this.note.textContent = message;
    }

    clearNote() {
      if (!this.note) return;

      this.note.hidden = true;
      this.note.textContent = '';
    }

    createMixId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      return `az-mix-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    onFormSubmit(e) {
      const shouldHijack =
        this.enabled &&
        this.mixCount > 1;

      if (!shouldHijack) return;

      const submitter = e.submitter || document.activeElement;

      if (
        submitter &&
        (
          submitter.matches('[increase-value], [decrease-value]') ||
          submitter.closest('.quantity, quantity-input, quantity-selector, [data-quantity], .quantity-selector, .product-quantity')
        )
      ) {
        e.preventDefault();
        e.stopPropagation();

        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        return;
      }

      const isMainATC =
        submitter &&
        submitter.matches('button[type="submit"]') &&
        (
          submitter.getAttribute('name') === 'add' ||
          submitter.classList.contains('product-form__submit') ||
          (submitter.id && submitter.id.toLowerCase().includes('submit')) ||
          submitter.hasAttribute('data-product-atc')
        );

      if (!isMainATC) return;

      e.preventDefault();
      e.stopPropagation();

      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      if (this._bundleAddInFlight) return;

      this.onAdd();
    }

    async onAdd() {
      if (this._bundleAddInFlight) return;

      this._bundleAddInFlight = true;

      this.clearNote();

      const selects = Array.from(this.querySelectorAll('select[mmf-select]'));
      const selectedVariantIds = selects.map((s) => s.value).filter(Boolean);

      if (selectedVariantIds.length !== this.mixCount) {
        this.setNote(`Please choose ${this.mixCount} flavours to continue.`);
        this._bundleAddInFlight = false;
        return;
      }

      if (!this.allowDuplicates) {
        const unique = new Set(selectedVariantIds);

        if (unique.size !== selectedVariantIds.length) {
          this.setNote('Please choose different flavours for each selection.');
          this._bundleAddInFlight = false;
          return;
        }
      }

      const sellingPlanId = this.getSellingPlanId();
      const subscriptionProperties = sellingPlanId ? this.getSubscriptionProperties() : [];
      const form = this.getProductForm();
      const mixId = this.createMixId();

      const sourceVariantId =
        (this.currentVariant && this.currentVariant.id) ||
        (
          form && form.querySelector('[name="id"]')
            ? form.querySelector('[name="id"]').value
            : null
        );

      if (!sourceVariantId) {
        this.setNote(
          'The selected size could not be identified. Please refresh the page and try again.'
        );

        this._bundleAddInFlight = false;
        return;
      }

      const addBtn =
        (form && form.querySelector('[type="submit"]')) ||
        this.addBtn;

      const fd = new FormData();

      selectedVariantIds.forEach((id, idx) => {
        fd.append(`items[${idx}][id]`, id);
        fd.append(`items[${idx}][quantity]`, '1');

        if (sellingPlanId) {
          fd.append(`items[${idx}][selling_plan]`, sellingPlanId);
        }

        subscriptionProperties.forEach((property) => {
          fd.append(`items[${idx}][properties][${property.key}]`, property.value);
        });

        /*
         * Private line-item properties used by the Shopify Discount Function.
         *
         * These identify the mix-and-match selection but do not determine its
         * price. The authoritative target price must be resolved by Jay's
         * Shopify app/Function rather than being supplied by the browser.
         */
        fd.append(
          `items[${idx}][properties][_az_mix_id]`,
          mixId
        );

        fd.append(
          `items[${idx}][properties][_az_mix_source_variant_id]`,
          String(sourceVariantId)
        );

        fd.append(
          `items[${idx}][properties][_az_mix_expected_quantity]`,
          String(this.mixCount)
        );

        fd.append(
          `items[${idx}][properties][_az_mix_version]`,
          '1'
        );
      });

      const cartType = 'cart-drawer';

      const helper =
        typeof HELPER_UTIL !== 'undefined'
          ? HELPER_UTIL
          : (window.HELPER_UTIL || null);

      const sectionRender =
        helper && helper.sectionRenderUtil
          ? helper.sectionRenderUtil(cartType)
          : null;

      const sectionsString =
        sectionRender && sectionRender.getSectionsString
          ? sectionRender.getSectionsString()
          : null;

      if (sectionsString) {
        fd.append('sections', sectionsString);

        const variantId =
          (this.currentVariant && this.currentVariant.id) ||
          (form && form.querySelector('[name="id"]') ? form.querySelector('[name="id"]').value : null);

        if (variantId) {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

          fd.append('sections_url', `${root}variants/${variantId}`);
        }
      }

      try {
        if (addBtn && helper && helper.submitButtonState) {
          helper.submitButtonState(addBtn).loadingState();
        } else {
          if (addBtn) addBtn.setAttribute('aria-busy', 'true');
          if (addBtn) addBtn.setAttribute('disabled', 'disabled');
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: fd,
        });

        if (!res.ok) {
          let msg = 'Something went wrong. Please try again.';

          try {
            const err = await res.json();
            if (err && err.description) msg = err.description;
          } catch {}

          this.setNote(msg);

          if (helper && helper.dispatchCustomEvent) {
            helper.dispatchCustomEvent(TOAST_EVENT, document, {
              toastType: 'common-errors',
              messageType: 'error',
              message: msg,
            });
          }

          return;
        }

        const data = await res.json();

        if (data && data.sections && helper && helper.dispatchCustomEvent) {
          helper.dispatchCustomEvent(TRIGGER_CART_CHANGE_EVENT, addBtn || this, {
            sections: data.sections,
          });
        } else {
          window.location.href = `${root}cart`;
        }
      } finally {
        if (addBtn && helper && helper.submitButtonState) {
          const state = helper.submitButtonState(addBtn);

          if (state && state.completeState) state.completeState();
          if (state && state.resetState) state.resetState(0);
        } else {
          if (addBtn) addBtn.removeAttribute('aria-busy');
          if (addBtn) addBtn.removeAttribute('disabled');
        }

        this._bundleAddInFlight = false;
      }
    }
  }

  if (!customElements.get('mix-match-flavours')) {
    customElements.define('mix-match-flavours', MixMatchFlavours);
  }
})();